import { ProgressSpinner } from "primereact/progressspinner";
import { createContext, useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  login as authSliceLogin,
  logout as authSliceLogout,
} from "../redux/features/authSlice";
import {
  authApi,
  useLogoutMutation,
  useVerifyTokenQuery,
} from "../redux/services/auth";

export interface RootAuthState {
  auth: {
    authenticated: boolean | undefined;
    userInfo: any;
  };
}

export interface AuthContextType {
  authenticated: boolean;
  logout: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [logout, { isLoading: isLoggingOut }] = useLogoutMutation();
  const authState = useSelector((state: RootAuthState) => state.auth);
  const [isLoggingOutState, setIsLoggingOutState] = useState(false);

  const shouldSkipVerification = [
    "/login",
    "/sign-up",
    "/forgot-password",
    "/no-access",
  ].includes(location.pathname);

  const handleLogout = async () => {
    try {
      // Set logging out state to prevent verification loading screen
      setIsLoggingOutState(true);

      await logout().unwrap();

      // Reset Redux state
      dispatch(authSliceLogout());

      // Reset RTK Query cache
      dispatch(authApi.util.resetApiState());

      navigate("/login");
    } catch (error) {
      // Even if the API call fails, still reset state and redirect
      dispatch(authSliceLogout());
      dispatch(authApi.util.resetApiState());
      navigate("/login");
    }
  };

  const {
    data: verifyAuthResult,
    isSuccess: verifyAuthSuccess,
    isLoading: verifyAuthIsLoading,
    error: verifyAuthError,
  } = useVerifyTokenQuery(undefined, {
    skip: shouldSkipVerification || isLoggingOutState,
  });

  useEffect(() => {
    if (verifyAuthSuccess && !isLoggingOutState) {
      dispatch(authSliceLogin());
    }
  }, [verifyAuthSuccess]);

  // Show loading state during verification, but only if not logging out
  if (verifyAuthIsLoading && !isLoggingOutState) {
    return (
      <div
        className="flex flex-column align-items-center justify-content-center"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          zIndex: 9999,
          animation: "fadeIn 1s ease 300ms forwards",
          opacity: 0,
        }}
      >
        <ProgressSpinner style={{ width: "50px", height: "50px" }} />
        <div className="mt-3 text-lg font-medium">
          Verifying authentication...
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider
      value={{
        authenticated: authState.authenticated,
        logout: handleLogout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

export default AuthProvider;
